import { BRAND_LABELS } from '@/defs/brand'
import { type IconType } from '@/defs/icon'
import { type LabelType } from '@/defs/misc'
import { PRICE_SOURCES } from '@/defs/price'
import { type RecRoleType, type RecType } from '@/defs/rec'
import { type StatType } from '@/defs/stat'
import { recordFromArray } from '@/utils/commonUtil'

export const GALLERY_VIEW_SLUGS = ['stats', 'cards', 'table', 'text'] as const
export type GalleryViewSlugType = (typeof GALLERY_VIEW_SLUGS)[number]
type GalleryViewLabelType = LabelType<GalleryViewSlugType, { icon: IconType }>
export const GALLERY_VIEW_LABELS: GalleryViewLabelType = {
  cards: { icon: 'image', label: 'Image' },
  stats: { icon: 'chartSimple', label: 'Stats' },
  table: { icon: 'table', label: 'Table' },
  text: { icon: 'font', label: 'Text' },
}
export const GALLERY_VIEW_SLUGS_SECTIONED: GalleryViewSlugType[] = ['cards', 'stats']

export const GALLERY_SIZE_SLUGS = ['sm', 'lg'] as const
export type GallerySizeSlugType = (typeof GALLERY_SIZE_SLUGS)[number]
type GallerySizeLabelType = LabelType<GallerySizeSlugType, { icon: IconType }>
export const GALLERY_SIZE_LABELS: GallerySizeLabelType = {
  lg: { icon: 'tableCellsLarge', label: 'Large' },
  sm: { icon: 'tableCells', label: 'Small' },
}
export const isGallerySizeSlugType = (slug?: string): slug is GallerySizeSlugType =>
  GALLERY_SIZE_SLUGS.includes(slug as GallerySizeSlugType)
export const isGalleryViewSizeable = (view: GalleryViewSlugType) => ['stats', 'cards'].includes(view)

export const GALLERY_GROUPBY_SLUGS = [
  'category',
  'type',
  'typeSimple',
  'mv',
  'price',
  'rarity',
  'releaseDate',
  'ungrouped',
] as const
export type GalleryGroupbySlugType = (typeof GALLERY_GROUPBY_SLUGS)[number]
type GalleryGroupbyLabelType = LabelType<GalleryGroupbySlugType, { icon: IconType; labelShort?: string }>
export const GALLERY_GROUPBY_LABELS: GalleryGroupbyLabelType = {
  category: { icon: 'tableList', label: 'Category' },
  mv: { icon: 'calculator', label: 'Mana Value', labelShort: 'MV' },
  price: { icon: 'dollarSign', label: 'Price' },
  rarity: { icon: 'star', label: 'Rarity' },
  releaseDate: { icon: 'calendar', label: 'Released' },
  type: { icon: 'hashtag', label: 'Type' },
  typeSimple: { icon: 'scroll', label: 'Spell/Land' },
  ungrouped: { icon: 'tableCellsLarge', label: 'Ungrouped' },
}
export const GALLERY_GROUPBY_SLUGS_CARD_REQUIRED: GalleryGroupbySlugType[] = [
  'type',
  'typeSimple',
  'mv',
  'price',
  'rarity',
  'releaseDate',
] as const

export const GALLERY_SORTBY_SLUGS_DEFAULT = [
  'default',
  'name',
  'mv',
  'price-preferred',
  'releaseDate',
  'salt',
  'type',
  'rarity',
  'colorId',
] as const
const GALLERY_SORTBY_SLUGS_PRICES = PRICE_SOURCES.map((ps) => `price-${ps}` as const)
export const GALLERY_SORTBY_SLUGS_DEFAULT_HIDDEN = [...GALLERY_SORTBY_SLUGS_PRICES] as const
export type GallerySortbySlugsDefaultHiddenType = (typeof GALLERY_SORTBY_SLUGS_DEFAULT_HIDDEN)[number]
export const isGallerySortbySlugsDefaultHidden = (slug?: string): slug is GallerySortbySlugsDefaultHiddenType =>
  GALLERY_SORTBY_SLUGS_DEFAULT_HIDDEN.includes(slug as GallerySortbySlugsDefaultHiddenType)
export const GALLERY_SORTBY_SLUGS_ADDITIONAL = [
  'count',
  'inclusion',
  'potentialDecks',
  'spoilDate',
  'rank',
  'synergy',
  'lift',
] as const
export const GALLERY_SORTBY_SLUGS = [
  ...GALLERY_SORTBY_SLUGS_DEFAULT,
  ...GALLERY_SORTBY_SLUGS_DEFAULT_HIDDEN,
  ...GALLERY_SORTBY_SLUGS_ADDITIONAL,
] as const
export type GallerySortbySlugType = (typeof GALLERY_SORTBY_SLUGS)[number]
type GallerySortbyLabelType = LabelType<GallerySortbySlugType, { icon: IconType; labelShort?: string }>
export const GALLERY_SORTBY_LABELS: GallerySortbyLabelType = {
  colorId: { icon: 'mtg-wubrg', label: 'Color ID' },
  count: { icon: 'layerGroup', label: 'Deck Count', labelShort: 'Decks' },
  default: { icon: 'cards', label: 'Default' },
  inclusion: { icon: 'percent', label: 'Inclusion' },
  lift: { icon: 'anglesUp', label: 'Lift' },
  mv: { icon: 'calculator', label: 'Mana Value', labelShort: 'MV' },
  name: { icon: 'font', label: 'Name' },
  potentialDecks: { icon: 'question', label: 'Potential Decks' },
  'price-preferred': { icon: 'dollarSign', label: 'Price' },
  rank: { icon: 'chessKing', label: 'Rank' },
  rarity: { icon: 'star', label: 'Rarity' },
  releaseDate: { icon: 'calendar', label: 'Release Date', labelShort: 'Date' },
  salt: { icon: 'salt', label: 'Salt' },
  spoilDate: { icon: 'calendar', label: 'Spoil Date', labelShort: 'Date' },
  synergy: { icon: 'wandMagicSparkles', label: 'Synergy' },
  type: { icon: 'hashtag', label: 'Card Type' },
  ...recordFromArray(PRICE_SOURCES, (ps) => [
    `price-${ps}`,
    {
      icon: BRAND_LABELS[ps].icon,
      label: BRAND_LABELS[ps].labelLong || BRAND_LABELS[ps].label,
    },
  ]),
}
export const isSortbySlugType = (slug: string): slug is GallerySortbySlugType =>
  GALLERY_SORTBY_SLUGS.includes(slug as GallerySortbySlugType)

export const GALLERY_SORTBY_SLUGS_CARD_REQUIRED: GallerySortbySlugType[] = [
  'colorId',
  'mv',
  'price-preferred',
  'releaseDate',
  'salt',
  'spoilDate',
  'type',
  'rarity',
  ...GALLERY_SORTBY_SLUGS_PRICES,
] as const

export const GALLERY_SORTBY_STATS: Partial<Record<GallerySortbySlugType, [StatType, ...StatType[]]>> = {
  colorId: ['colorId'],
  count: ['deckTotal', 'deckTotalCommander', 'deckTotalFull', 'deckFraction'],
  inclusion: ['inclusion', 'inclusionGraphic', 'inclusionAdded', 'inclusionCut'],
  lift: ['lift'],
  rank: ['rank'],
  releaseDate: ['released'],
  salt: ['salt'],
  spoilDate: ['spoiled'],
  synergy: ['synergy'],
}

export const GALLERY_SORTBY_DIRECTION = ['desc', 'asc'] as const
export type GallerySortbyDirectionType = (typeof GALLERY_SORTBY_DIRECTION)[number]
type GallerySortbyDirectionLabelType = LabelType<GallerySortbyDirectionType, { icon: IconType }>
export const GALLERY_SORTBY_DIRECTION_LABELS: GallerySortbyDirectionLabelType = {
  asc: { icon: 'arrowUp', label: 'Ascending' },
  desc: { icon: 'arrowDown', label: 'Descending' },
}

export const GALLERY_LOCK_SLUGS = ['cardFilters', 'groupby', 'sortby'] as const
export type GalleryLockSlugType = (typeof GALLERY_LOCK_SLUGS)[number]
type GalleryLockLabelType = LabelType<GalleryLockSlugType>
export const GALLERY_LOCK_LABELS: GalleryLockLabelType = {
  cardFilters: { label: 'Card Filters' },
  groupby: { label: 'Group By' },
  sortby: { label: 'Sort By' },
}

export const GALLERY_GROUP_SLUG_UNGROUPED = 'ungrouped' as const
export const GALLERY_GROUP_SLUGS_TYPE = [
  'type-commander',
  'type-creature',
  'type-instant',
  'type-sorcery',
  'type-artifact',
  'type-enchantment',
  'type-battle',
  'type-planeswalker',
  'type-land',
] as const
export const GALLERY_GROUP_SLUGS_TYPE_SIMPLE = ['type-spell', 'type-land'] as const
export const GALLERY_GROUP_SLUGS_MV = [
  'mv-0',
  'mv-1',
  'mv-2',
  'mv-3',
  'mv-4',
  'mv-5',
  'mv-6',
  'mv-7',
  'mv-8',
  'mv-9',
  'mv-10',
  'mv-11',
  'mv-12',
  'mv-13',
  'mv-14',
  'mv-15+',
  'mv-land',
] as const
export const GALLERY_GROUP_SLUGS_PRICE = [
  'price-less-than-fifty-cents',
  'price-fifty-cents-to-ninety-nine-cents',
  'price-one-dollar-to-two-dollars-forty-nine',
  'price-two-dollars-fifty-to-four-dollars-ninety-nine',
  'price-five-dollars-to-nine-dollars-ninety-nine',
  'price-ten-dollars-to-nineteen-dollars-ninety-nine',
  'price-twenty-dollars-to-forty-nine-dollars-ninety-nine',
  'price-fifty-dollars-to-ninety-nine-dollars-ninety-nine',
  'price-one-hundred-dollars-or-more',
] as const
export const GALLERY_GROUP_SLUGS_RARITY = [
  'rarity-mythic',
  'rarity-rare',
  'rarity-uncommon',
  'rarity-common',
  'rarity-other',
] as const
export const GALLERY_GROUP_SLUGS_RELEASE_DATE = [
  'date-last-3-months',
  'date-last-6-months',
  'date-last-year',
  'date-last-2-years',
  'date-since-2020',
  'date-since-2010',
  'date-since-2000',
  'date-earliest',
] as const
export const GALLERY_GROUP_SLUGS_DEFAULTS = [
  GALLERY_GROUP_SLUG_UNGROUPED,
  ...GALLERY_GROUP_SLUGS_TYPE,
  ...GALLERY_GROUP_SLUGS_TYPE_SIMPLE,
  ...GALLERY_GROUP_SLUGS_MV,
  ...GALLERY_GROUP_SLUGS_PRICE,
  ...GALLERY_GROUP_SLUGS_RARITY,
  ...GALLERY_GROUP_SLUGS_RELEASE_DATE,
] as const
export type GalleryGroupSlugType = (typeof GALLERY_GROUP_SLUGS_DEFAULTS)[number]
export const isGalleryGroupSlugType = (slug: string): slug is GalleryGroupSlugType =>
  GALLERY_GROUP_SLUGS_DEFAULTS.includes(slug as GalleryGroupSlugType)

export type GalleryGroupSlugLabelType = LabelType<GalleryGroupSlugType, { icon: IconType }>
export const GALLERY_GROUP_LABELS: GalleryGroupSlugLabelType = {
  // eslint-disable-next-line sort/object-properties
  ungrouped: { icon: 'cards', label: 'All Cards' },

  'type-commander': { icon: 'chessKing', label: 'Commander' },
  'type-creature': { icon: 'type-creature', label: 'Creature' },
  'type-instant': { icon: 'type-instant', label: 'Instant' },
  'type-sorcery': { icon: 'type-sorcery', label: 'Sorcery' },
  'type-artifact': { icon: 'type-artifact', label: 'Artifact' },
  'type-enchantment': { icon: 'type-enchantment', label: 'Enchantment' },
  'type-battle': { icon: 'type-battle', label: 'Battle' },
  'type-planeswalker': { icon: 'type-planeswalker', label: 'Planeswalker' },
  'type-land': { icon: 'type-land', label: 'Land' },

  'type-spell': { icon: 'hatWizard', label: 'Spell' },

  'mv-0': { icon: 'mtg-0', label: '0 Mana' },
  'mv-1': { icon: 'mtg-1', label: '1 Mana' },
  'mv-2': { icon: 'mtg-2', label: '2 Mana' },
  'mv-3': { icon: 'mtg-3', label: '3 Mana' },
  'mv-4': { icon: 'mtg-4', label: '4 Mana' },
  'mv-5': { icon: 'mtg-5', label: '5 Mana' },
  'mv-6': { icon: 'mtg-6', label: '6 Mana' },
  'mv-7': { icon: 'mtg-7', label: '7 Mana' },
  'mv-8': { icon: 'mtg-8', label: '8 Mana' },
  'mv-9': { icon: 'mtg-9', label: '9 Mana' },
  'mv-10': { icon: 'mtg-10', label: '10 Mana' },
  'mv-11': { icon: 'mtg-11', label: '11 Mana' },
  'mv-12': { icon: 'mtg-12', label: '12 Mana' },
  'mv-13': { icon: 'mtg-13', label: '13 Mana' },
  'mv-14': { icon: 'mtg-14', label: '14 Mana' },
  'mv-15+': { icon: 'mtg-15', label: '15+ Mana' },
  'mv-land': { icon: 'type-land', label: 'Land' },

  'price-less-than-fifty-cents': { icon: 'coins', label: '< $0.50' },
  'price-fifty-cents-to-ninety-nine-cents': { icon: 'coins', label: '$0.50 - $0.99' },
  'price-one-dollar-to-two-dollars-forty-nine': { icon: 'moneyBillWave', label: '$1.00 - $2.49' },
  'price-two-dollars-fifty-to-four-dollars-ninety-nine': { icon: 'moneyBillWave', label: '$2.50 - $4.99' },
  'price-five-dollars-to-nine-dollars-ninety-nine': { icon: 'sackDollar', label: '$5.00 - $9.99' },
  'price-ten-dollars-to-nineteen-dollars-ninety-nine': { icon: 'sackDollar', label: '$10.00 - $19.99' },
  'price-twenty-dollars-to-forty-nine-dollars-ninety-nine': { icon: 'house', label: '$20.00 - $49.99' },
  'price-fifty-dollars-to-ninety-nine-dollars-ninety-nine': { icon: 'house', label: '$50.00 - $99.99' },
  'price-one-hundred-dollars-or-more': { icon: 'landmark', label: '$100.00+' },

  'rarity-mythic': { icon: 'm', label: 'Mythic' },
  'rarity-rare': { icon: 'r', label: 'Rare' },
  'rarity-uncommon': { icon: 'u', label: 'Uncommon' },
  'rarity-common': { icon: 'c', label: 'Common' },
  'rarity-other': { icon: 's', label: 'Other' },

  'date-last-3-months': { icon: 'fireFlameSimple', label: 'Last 3 months' },
  'date-last-6-months': { icon: 'fire', label: 'Last 6 months' },
  'date-last-year': { icon: 'stopwatch', label: 'Last year' },
  'date-last-2-years': { icon: 'clock', label: 'Last 2 years' },
  'date-since-2020': { icon: 'hourglassStart', label: 'Since 2020' },
  'date-since-2010': { icon: 'hourglassHalf', label: 'Since 2010' },
  'date-since-2000': { icon: 'hourglassEnd', label: 'Since 2000' },
  'date-earliest': { icon: 'gem', label: 'Earliest' },
}

export type GalleryGroupType = {
  icon: IconType
  label: string
  recs: RecType[]
  slug: string
}

/* Gallery Table Columns */
export const GALLERY_COLUMN_SLUGS_CORE = ['colorId', 'cmc', 'name'] as const
type GalleryColumnSlugsCoreType = (typeof GALLERY_COLUMN_SLUGS_CORE)[number]
export const isGalleryColumnSlugsCoreType = (slug?: string): slug is GalleryColumnSlugsCoreType =>
  GALLERY_COLUMN_SLUGS_CORE.includes(slug as GalleryColumnSlugsCoreType)

export const GALLERY_COLUMN_SLUGS_META = ['rarity', 'salt', 'type'] as const
const GALLERY_COLUMN_SLUGS_STATS = [
  'stat-count',
  'stat-inclusion',
  'stat-potentialDecks',
  'stat-lift',
  'stat-rank',
  'stat-synergy',
] as const
const GALLERY_COLUMN_SLUGS_PRICES = PRICE_SOURCES.map((ps) => `price-${ps}` as const)

export const GALLERY_COLUMN_SLUGS = [
  ...GALLERY_COLUMN_SLUGS_CORE,
  ...GALLERY_COLUMN_SLUGS_META,
  ...GALLERY_COLUMN_SLUGS_STATS,
  ...GALLERY_COLUMN_SLUGS_PRICES,
] as const
export type GalleryColumnSlugType = (typeof GALLERY_COLUMN_SLUGS)[number]
export const isGalleryColumnSlugType = (slug: string): slug is GalleryColumnSlugType =>
  GALLERY_COLUMN_SLUGS.includes(slug as GalleryColumnSlugType)

export type GalleryDisplayType = {
  cardFiltersCount: number
  columns: GalleryColumnSlugType[]
  columnsOptions: GalleryColumnSlugType[]
  edited: {
    cardFilters: boolean
    columns: boolean
    groupby: boolean
    size: boolean
    sortby: boolean
    sortbyDirection: boolean
    view: boolean
  }
  editedCount: number
  groupby: GalleryGroupbySlugType
  groupbyDefault: GalleryGroupbySlugType
  groupbySlugs: GalleryGroupbySlugType[]
  size: GallerySizeSlugType
  sortby: GallerySortbySlugType
  sortbyDefault: GallerySortbySlugType
  sortbyDefaultIcon: IconType
  sortbyDefaultLabel: string
  sortbyDirection: GallerySortbyDirectionType
  sortbyOptions: GallerySortbySlugType[]
  stats?: StatType[]
  statsRoles?: Partial<Record<RecRoleType, StatType[]>>
  view: GalleryViewSlugType
}

export const GALLERY_DISPLAY_DEFAULT: GalleryDisplayType = {
  cardFiltersCount: 0,
  columns: [],
  columnsOptions: [...GALLERY_COLUMN_SLUGS],
  edited: {
    cardFilters: false,
    columns: false,
    groupby: false,
    size: false,
    sortby: false,
    sortbyDirection: false,
    view: false,
  },
  editedCount: 0,
  groupby: 'category',
  groupbyDefault: 'category',
  groupbySlugs: [...GALLERY_GROUPBY_SLUGS],
  size: 'lg',
  sortby: 'default',
  sortbyDefault: 'default',
  sortbyDefaultIcon: 'cards',
  sortbyDefaultLabel: 'Default',
  sortbyDirection: 'desc',
  sortbyOptions: [...GALLERY_SORTBY_SLUGS_DEFAULT, ...GALLERY_SORTBY_SLUGS_DEFAULT_HIDDEN],
  view: 'stats',
} as const
